import moment from 'moment'
import {ListObjectEnum, ListObjectIdEnum} from 'src/pages/alarms/AlarmsFunctions'
import {FilterEventsType, SearchEventsType} from 'src/services/api'
import {parseUnifiedEventsSearchParam, type EventsSearchField} from './parse-unified-events-search-param'

const EVENT_STATUS_LIST = [
  {value: '1', label: 'Iniciado'},
  {value: '2', label: 'Processado'},
  {value: '3', label: 'Ignorado'},
  {value: '4', label: 'Expirado'},
  {value: '5', label: 'Erro'},
  {value: '6', label: 'Despachado'},
  {value: '7', label: 'Correto'},
  {value: '8', label: 'Incorreto'},
] as const

const DETECTION_TYPE_BY_ID: Record<string, ListObjectEnum> = {
  [ListObjectIdEnum.OBJECT_DETECTION]: ListObjectEnum.OBJECT_DETECTION,
  [ListObjectIdEnum.LPR]: ListObjectEnum.LPR,
  [ListObjectIdEnum.LINE_CROSSING]: ListObjectEnum.LINE_CROSSING,
  [ListObjectIdEnum.FACIAL_RECOGNITION]: ListObjectEnum.FACIAL_RECOGNITION,
  [ListObjectIdEnum.SCENE_CHANGE]: ListObjectEnum.SCENE_CHANGE,
  [ListObjectIdEnum.SPEED]: ListObjectEnum.SPEED,
  [ListObjectIdEnum.EPI]: ListObjectEnum.EPI,
  [ListObjectIdEnum.HOODIE]: ListObjectEnum.HOODIE,
  [ListObjectIdEnum.SHOPPING_CART]: ListObjectEnum.SHOPPING_CART,
}

type PresetValue = '15m' | '30m' | '1h' | '6h' | '12h' | 'today' | 'monthly'

export type ParsedEventsUrlFilters = {
  filter: Pick<FilterEventsType, 'from' | 'to' | 'statuses' | 'eventTypeIds'>
  search: SearchEventsType
  searchField?: EventsSearchField
  searchInput?: string
  statusLabels: string[]
  typeLabels: string[]
}

function resolvePresetRange(preset: PresetValue, now = new Date()) {
  const to = new Date(now)
  const from = new Date(now)

  switch (preset) {
    case '15m':
      from.setMinutes(from.getMinutes() - 15)
      break
    case '30m':
      from.setMinutes(from.getMinutes() - 30)
      break
    case '1h':
      from.setHours(from.getHours() - 1)
      break
    case '6h':
      from.setHours(from.getHours() - 6)
      break
    case '12h':
      from.setHours(from.getHours() - 12)
      break
    case 'today':
      from.setHours(0, 0, 0, 0)
      break
    case 'monthly':
      from.setDate(1)
      from.setHours(0, 0, 0, 0)
      break
  }

  return {
    from: moment(from).format(),
    to: moment(to).format(),
  }
}

function parseCreatedAtParam(value: string | null): {from: string; to: string} | null {
  if (!value) return null

  if (value.startsWith('preset:')) {
    const preset = value.slice('preset:'.length) as PresetValue
    if (!['15m', '30m', '1h', '6h', '12h', 'today', 'monthly'].includes(preset)) return null
    return resolvePresetRange(preset)
  }

  if (value.startsWith('custom:')) {
    const range = value.slice('custom:'.length)
    const [fromRaw, toRaw] = range.split(',')
    const fromMs = fromRaw ? Number(fromRaw) : NaN
    const toMs = toRaw ? Number(toRaw) : NaN
    if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return null

    return {
      from: moment(new Date(fromMs)).format(),
      to: moment(new Date(toMs)).format(),
    }
  }

  return null
}

function mapStatusIdsToLabels(statusIds: string[]): string[] {
  return statusIds.map((id) => EVENT_STATUS_LIST.find((item) => item.value === id)?.label.toUpperCase()).filter((label): label is string => Boolean(label))
}

function mapTypeIdsToLabels(typeIds: string[]): string[] {
  return typeIds.map((id) => DETECTION_TYPE_BY_ID[id]).filter((label): label is ListObjectEnum => Boolean(label))
}

function buildUnifiedSearchParam(search: SearchEventsType): string | undefined {
  if (search.id != null) return String(search.id)
  if (search.cameraName?.trim()) return search.cameraName.trim()
  if (search.clientName?.trim()) return search.clientName.trim()
  return undefined
}

function isNearPreset30m(from?: string, to?: string) {
  if (!from || !to) return false
  const expected = resolvePresetRange('30m')
  const fromDelta = Math.abs(new Date(from).getTime() - new Date(expected.from).getTime())
  const toDelta = Math.abs(new Date(to).getTime() - new Date(expected.to).getTime())
  return fromDelta < 60_000 && toDelta < 60_000
}

export function parseEventsUrlSearchParams(search: string): ParsedEventsUrlFilters | null {
  const trimmed = search.startsWith('?') ? search.slice(1) : search
  if (!trimmed) return null

  const params = new URLSearchParams(trimmed)
  const result: ParsedEventsUrlFilters = {
    filter: {},
    search: {},
    statusLabels: [],
    typeLabels: [],
  }

  const createdAtRange =
    parseCreatedAtParam(params.get('createdAt')) ?? (params.get('from') && params.get('to') ? {from: params.get('from')!, to: params.get('to')!} : null)

  if (createdAtRange) {
    result.filter.from = createdAtRange.from
    result.filter.to = createdAtRange.to
  }

  const hasCanonicalFilterParams =
    params.has('search') ||
    params.has('createdAt') ||
    params.has('status') ||
    params.has('type') ||
    params.has('from') ||
    params.has('to') ||
    params.has('camera') ||
    params.has('id')

  const statusIds = params.getAll('status')
  if (statusIds.length) {
    result.filter.statuses = mapStatusIdsToLabels(statusIds)
    result.statusLabels = result.filter.statuses.map((label) => label.charAt(0) + label.slice(1).toLowerCase())
  } else if (hasCanonicalFilterParams) {
    result.filter.statuses = []
  }

  const typeIds = params.getAll('type').flatMap((value) =>
    value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean),
  )
  if (typeIds.length) {
    result.filter.eventTypeIds = typeIds
    result.typeLabels = mapTypeIdsToLabels(typeIds)
  }

  const cameraQuery = params.get('camera')?.trim()
  const idQuery = params.get('id')?.trim()
  const unifiedSearch = params.get('search')?.trim()

  if (cameraQuery) {
    result.search = {cameraName: cameraQuery}
    result.searchField = 'cameraName'
    result.searchInput = cameraQuery
  } else if (idQuery) {
    const id = Number(idQuery)
    if (!Number.isNaN(id)) {
      result.search = {id}
      result.searchField = 'id'
      result.searchInput = idQuery
    }
  } else if (unifiedSearch) {
    const parsed = parseUnifiedEventsSearchParam(unifiedSearch)
    if (parsed) {
      result.searchField = parsed.field
      result.searchInput = parsed.value
      result.search = parsed.field === 'id' ? {id: Number(parsed.value)} : {[parsed.field]: parsed.value}
    }
  }

  const hasFilters =
    hasCanonicalFilterParams ||
    result.filter.from ||
    result.filter.to ||
    result.filter.statuses?.length ||
    result.filter.eventTypeIds?.length ||
    result.searchField

  return hasFilters ? result : null
}

export function buildEventsUrlSearchParams(input: {filter: FilterEventsType; search: SearchEventsType}): string {
  const params = new URLSearchParams()

  const unifiedSearch = buildUnifiedSearchParam(input.search)
  if (unifiedSearch) params.set('search', unifiedSearch)

  if (input.filter.from && input.filter.to) {
    if (isNearPreset30m(input.filter.from, input.filter.to)) {
      params.set('createdAt', 'preset:30m')
    } else {
      const fromMs = new Date(input.filter.from).getTime()
      const toMs = new Date(input.filter.to).getTime()
      if (!Number.isNaN(fromMs) && !Number.isNaN(toMs)) {
        params.set('createdAt', `custom:${fromMs},${toMs}`)
      }
    }
  }

  input.filter.statuses?.forEach((statusLabel) => {
    const statusId = EVENT_STATUS_LIST.find((item) => item.label.toUpperCase() === statusLabel)?.value
    if (statusId) params.append('status', statusId)
  })

  input.filter.eventTypeIds?.forEach((typeId) => {
    params.append('type', typeId)
  })

  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export function normalizeEventsSearchParams(search: string): string {
  const parsed = parseEventsUrlSearchParams(search)
  if (!parsed) return search.startsWith('?') ? search : search ? `?${search}` : ''
  return buildEventsUrlSearchParams({filter: parsed.filter, search: parsed.search})
}

export function isEventsRoutePath(pathname: string) {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return normalized === '/events'
}
